import { useState, useEffect, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import EmptyState from "./EmptyState";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");
const INR      = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const MONTHS   = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken(), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const CATS = [
  { name:"Rent & Office",       icon:"🏢", color:"#6366f1", keywords:["rent","office","maintenance","building","landlord"] },
  { name:"Salary & Wages",      icon:"👥", color:"#8b5cf6", keywords:["salary","wage","staff","payroll","neft","employee","worker"] },
  { name:"Packaging",           icon:"📦", color:"#F47920", keywords:["packaging","box","carton","tape","bubble","poly","bag","packing"] },
  { name:"Transport & Courier", icon:"🚚", color:"#0ea5e9", keywords:["shiprocket","delhivery","bluedart","dtdc","ekart","courier","transport","logistics","freight","shipping"] },
  { name:"Advertising",         icon:"📣", color:"#ec4899", keywords:["ads","advertising","facebook","google","meta","instagram","campaign","marketing","sponsored"] },
  { name:"Platform Fees",       icon:"🛒", color:"#14b8a6", keywords:["shopify","amazon","flipkart","myntra","meesho","commission","platform","marketplace","seller fee"] },
  { name:"Returns & Refunds",   icon:"↩️", color:"#f59e0b", keywords:["refund","return","reversal","chargeback","dispute","cancelled"] },
  { name:"Raw Material",        icon:"🧵", color:"#a16207", keywords:["leather","fabric","material","raw","belt","buckle","hardware","supplier","purchase","vendor"] },
  { name:"Utilities",           icon:"💡", color:"#22c55e", keywords:["electricity","water","internet","broadband","airtel","jio","mobile","recharge","bill","utility"] },
  { name:"Tax & Govt",          icon:"🏛️", color:"#dc2626", keywords:["gst","tds","income tax","challan","tax","govt","government","mca","epfo","esi"] },
  { name:"Bank Charges",        icon:"🏦", color:"#64748b", keywords:["bank charge","service charge","annual fee","interest","emi","loan","penalty","minimum balance"] },
  { name:"Sales / Payment In",  icon:"💰", color:"#15803d", keywords:["shopify","amazon","flipkart","myntra","meesho","razorpay","paytm","payment gateway","settlement","payout"] },
  { name:"Miscellaneous",       icon:"📝", color:"#94a3b8", keywords:[] },
];

const PAY_MODES = ["Cash","UPI","Bank Transfer","Card","Cheque","NEFT","RTGS","IMPS"];
const GST_RATES = [0,5,12,18,28];

function autoCategory(desc, type) {
  if (!desc) return "Miscellaneous";
  const d = desc.toLowerCase();
  if (type === "CR") {
    for (const cat of CATS) {
      if (cat.name === "Sales / Payment In" && cat.keywords.some((k) => d.includes(k))) return cat.name;
    }
    return "Sales / Payment In";
  }
  for (const cat of CATS) {
    if (cat.keywords.some((k) => d.includes(k))) return cat.name;
  }
  return "Miscellaneous";
}

function parseHDFCRows(rows) {
  const txns = [];
  for (const row of rows) {
    const cols = Array.isArray(row) ? row : Object.values(row);
    if (!cols[0] || !String(cols[0]).match(/\d{2}[\/\-]\d{2}[\/\-]\d{2,4}/)) continue;
    const dateStr = String(cols[0]).trim();
    const desc    = String(cols[1] || "").trim();
    let debit   = parseFloat(String(cols[4] || "0").replace(/,/g,"")) || 0;
    let credit  = parseFloat(String(cols[5] || "0").replace(/,/g,"")) || 0;
    const balance = parseFloat(String(cols[6] || "0").replace(/,/g,"")) || 0;
    if (!desc || (!debit && !credit)) continue;
    const parts = dateStr.split(/[\/\-]/);
    let date;
    try {
      const yr = parts[2].length === 2 ? "20"+parts[2] : parts[2];
      date = new Date(parseInt(yr), parseInt(parts[1])-1, parseInt(parts[0])).toISOString().split("T")[0];
    } catch { date = new Date().toISOString().split("T")[0]; }
    // Narration-based correction: HDFC marks incoming as "NEFT CR-", "IMPS-...CR", "UPI...CR"
    // and outgoing as "NEFT DR-" etc. If both amounts landed in one column by mistake,
    // the narration tells us the true direction.
    const narr = desc.toUpperCase();
    const saysCredit = /\bCR[-\s]|CREDIT|\bREV\b|REFUND|CASHBACK/.test(narr);
    const saysDebit  = /\bDR[-\s]|DEBIT|\bPUR\b|\bDR\//.test(narr);
    // If only one amount is present, trust it. If narration disagrees with which column
    // got the value, correct it.
    if (debit > 0 && credit === 0 && saysCredit && !saysDebit) { credit = debit; debit = 0; }
    if (credit > 0 && debit === 0 && saysDebit && !saysCredit) { debit = credit; credit = 0; }
    const type = credit > 0 ? "CR" : "DR";
    txns.push({ date, description:desc, type, amount:credit>0?credit:debit, balance, category:autoCategory(desc,type), payMode:"Bank Transfer", source:"statement" });
  }
  return txns;
}

function parseGenericRows(rows) {
  if (!rows.length) return [];

  // Bank statements often have several metadata rows (account no, period, address)
  // before the real column header. Scan the first 25 rows to find the header row
  // — the one that contains a date column AND a description/amount column.
  const looksLikeHeader = (arr) => {
    const cells = (Array.isArray(arr) ? arr : Object.values(arr)).map((h) => String(h).toLowerCase().trim());
    const hasDate = cells.some((h) => h.includes("date"));
    const hasDesc = cells.some((h) => ["description","narration","particulars","remarks","details","narrative"].some((k)=>h.includes(k)));
    const hasMoney = cells.some((h) => ["debit","credit","withdrawal","deposit","amount","balance"].some((k)=>h.includes(k)));
    return hasDate && (hasDesc || hasMoney);
  };

  let headerRowIdx = 0;
  const scanLimit = Math.min(rows.length, 25);
  for (let i = 0; i < scanLimit; i++) {
    if (looksLikeHeader(rows[i])) { headerRowIdx = i; break; }
  }

  const headerRow = rows[headerRowIdx];
  const headers = (Array.isArray(headerRow) ? headerRow : Object.keys(headerRow)).map((h) => String(h).toLowerCase().trim());
  const fi = (kws) => headers.findIndex((h) => kws.some((k) => h.includes(k)));
  const dateIdx   = fi(["date","txn date","transaction date","value date"]);
  const descIdx   = fi(["description","narration","particulars","remarks","details","narrative"]);
  const debitIdx  = fi(["withdrawal amt","withdrawal","debit","dr","money out"]);
  const creditIdx = fi(["deposit amt","deposit","credit","cr","money in"]);
  const amtIdx    = fi(["amount","transaction amount"]);
  const balIdx    = fi(["balance","closing balance"]);
  const typeIdx   = fi(["type","cr/dr","transaction type","dr/cr"]);
  const txns = [];
  const dataRows = rows.slice(headerRowIdx + 1);
  for (const row of dataRows) {
    const cols   = Array.isArray(row) ? row : Object.values(row);
    const get    = (idx) => idx >= 0 && cols[idx] != null ? String(cols[idx]).trim() : "";
    const getNum = (idx) => parseFloat(get(idx).replace(/[,₹\s]/g,"")) || 0;
    // Skip separator rows (e.g. "********") and rows with no real date
    const firstCell = String(cols[0] || "").trim();
    if (/^\*+$/.test(firstCell) || firstCell === "") continue;
    const dateStr = get(dateIdx);
    if (!dateStr || !dateStr.match(/\d/)) continue;
    // Skip if the "date" is actually a separator
    if (/^\*+$/.test(dateStr)) continue;
    const desc = get(descIdx);
    if (!desc) continue;
    let debit  = debitIdx  >= 0 ? getNum(debitIdx)  : 0;
    let credit = creditIdx >= 0 ? getNum(creditIdx) : 0;
    let amount = amtIdx    >= 0 ? getNum(amtIdx)    : 0;
    const typeStr = get(typeIdx).toUpperCase();
    if (!debit && !credit && amount) {
      if (typeStr.includes("CR") || typeStr.includes("CREDIT") || typeStr.includes("IN")) credit = amount;
      else debit = amount;
    }
    if (!debit && !credit) continue;
    let date = dateStr;
    // Handle DD/MM/YY or DD/MM/YYYY (Indian bank format) explicitly
    const dm = dateStr.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (dm) {
      let [, dd, mm, yy] = dm;
      if (yy.length === 2) yy = "20" + yy;
      date = `${yy}-${mm.padStart(2,"0")}-${dd.padStart(2,"0")}`;
    } else {
      try { const d = new Date(dateStr); if (!isNaN(d)) date = d.toISOString().split("T")[0]; } catch {}
    }
    const type    = credit > 0 ? "CR" : "DR";
    const balance = getNum(balIdx);
    txns.push({ date, description:desc, type, amount:credit>0?credit:debit, balance, category:autoCategory(desc,type), payMode:"Bank Transfer", source:"statement" });
  }
  return txns;
}

async function parsePdfWithAI(file) {
  const base64 = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const response = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 4000,
      messages: [{
        role: "user",
        content: [
          { type:"document", source:{ type:"base64", media_type:"application/pdf", data:base64 } },
          { type:"text", text:`Extract ALL transactions from this bank statement. Return ONLY a JSON array, no markdown, no explanation. Each object must have: {"date":"YYYY-MM-DD","description":"narration text","type":"DR or CR","amount":positive_number,"balance":number_or_0}. Extract every transaction row. Return only the JSON array.` }
        ]
      }]
    })
  });
  const data = await response.json();
  const text  = data.content?.[0]?.text || "[]";
  const clean = text.replace(/```json|```/g,"").trim();
  const parsed = JSON.parse(clean);
  return parsed.map((t) => ({
    ...t,
    category: autoCategory(t.description, t.type),
    payMode:  "Bank Transfer",
    source:   "statement",
    amount:   Math.abs(parseFloat(t.amount) || 0),
  }));
}

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"7px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
  input:   { width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  label:   { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  th:      { padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb" },
  td:      { padding:"8px 12px", borderBottom:"1px solid #f3f4f6", fontSize:12, verticalAlign:"middle" },
};

function BarChart({ data, height=110 }) {
  if (!data?.length) return <div style={{ textAlign:"center", padding:20, color:"#9ca3af", fontSize:12 }}>No data</div>;
  const max = Math.max(...data.map((d) => Math.max(d.in||0, d.out||0)), 1);
  return (
    <div style={{ display:"flex", alignItems:"flex-end", gap:4, height, padding:"0 4px" }}>
      {data.map((d,i) => (
        <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
          <div style={{ width:"100%", display:"flex", gap:1, alignItems:"flex-end", height:height-20 }}>
            <div style={{ flex:1, background:"#22c55e", borderRadius:"2px 2px 0 0", height:Math.max(((d.in||0)/max)*(height-20),2) }}/>
            <div style={{ flex:1, background:"#F47920", borderRadius:"2px 2px 0 0", height:Math.max(((d.out||0)/max)*(height-20),2) }}/>
          </div>
          <div style={{ fontSize:8, color:"#9ca3af" }}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}

function AddExpenseModal({ onSave, onClose, toast }) {
  const [form, setForm] = useState({ date:new Date().toISOString().split("T")[0], description:"", category:"Miscellaneous", amount:"", type:"DR", gstRate:0, isGstInput:false, vendor:"", invoiceNo:"", payMode:"Cash", notes:"" });
  const set = (k,v) => setForm((f)=>({...f,[k]:v}));
  const gstAmt = form.gstRate && form.isGstInput && form.amount ? Math.round(parseFloat(form.amount)*form.gstRate/(100+parseFloat(form.gstRate))*100)/100 : 0;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
      <div style={{ background:"#fff", borderRadius:12, width:"100%", maxWidth:500, maxHeight:"90vh", overflow:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ padding:"14px 18px", borderBottom:"1px solid #e5e7eb", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontWeight:700, fontSize:15 }}>Add Cash Entry</span>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#6b7280" }}>×</button>
        </div>
        <div style={{ padding:18, display:"flex", flexDirection:"column", gap:12 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div><label style={S.label}>Date *</label><input type="date" style={S.input} value={form.date} onChange={(e)=>set("date",e.target.value)}/></div>
            <div><label style={S.label}>Amount (₹) *</label><input type="number" style={S.input} placeholder="0" value={form.amount} onChange={(e)=>set("amount",e.target.value)}/></div>
          </div>
          <div>
            <label style={S.label}>Type</label>
            <div style={{ display:"flex", gap:8 }}>
              {[["DR","💸 Expense (Money Out)"],["CR","💰 Income (Money In)"]].map(([v,l])=>(
                <button key={v} style={{ ...S.btn(form.type===v?(v==="DR"?"#F47920":"#15803d"):"#e5e7eb"), color:form.type===v?"#fff":"#374151", flex:1, fontSize:12 }} onClick={()=>set("type",v)}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <label style={S.label}>Description *</label>
            <input style={S.input} placeholder="What was this for?" value={form.description} onChange={(e)=>{ set("description",e.target.value); set("category",autoCategory(e.target.value,form.type)); }}/>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div>
              <label style={S.label}>Category</label>
              <select style={S.input} value={form.category} onChange={(e)=>set("category",e.target.value)}>
                {CATS.map((c)=><option key={c.name} value={c.name}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Payment Mode</label>
              <select style={S.input} value={form.payMode} onChange={(e)=>set("payMode",e.target.value)}>
                {PAY_MODES.map((m)=><option key={m}>{m}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Vendor</label><input style={S.input} placeholder="Supplier / payee" value={form.vendor} onChange={(e)=>set("vendor",e.target.value)}/></div>
            <div><label style={S.label}>Invoice No.</label><input style={S.input} placeholder="INV-001" value={form.invoiceNo} onChange={(e)=>set("invoiceNo",e.target.value)}/></div>
          </div>
          <div style={{ background:"#f0fdf4", borderRadius:8, padding:12, border:"1px solid #bbf7d0" }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer", marginBottom:8 }}>
              <input type="checkbox" checked={form.isGstInput} onChange={(e)=>set("isGstInput",e.target.checked)}/>
              GST Invoice — eligible for Input Tax Credit
            </label>
            {form.isGstInput && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div><label style={S.label}>GST Rate</label><select style={S.input} value={form.gstRate} onChange={(e)=>set("gstRate",parseFloat(e.target.value))}>{GST_RATES.map((r)=><option key={r} value={r}>{r}%</option>)}</select></div>
                <div><label style={S.label}>GST Amount (auto)</label><div style={{ ...S.input, background:"#f9fafb", color:"#15803d", fontWeight:600 }}>{INR(gstAmt)}</div></div>
              </div>
            )}
          </div>
          <div><label style={S.label}>Notes</label><textarea style={{ ...S.input, height:52, resize:"vertical" }} value={form.notes} onChange={(e)=>set("notes",e.target.value)}/></div>
          <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
            <button style={S.btnOut} onClick={onClose}>Cancel</button>
            <button style={S.btn()} onClick={()=>{ if(!form.description||!form.amount){toast("Fill description and amount","error");return;} onSave({...form,amount:parseFloat(form.amount),gstAmount:gstAmt,source:"manual"}); onClose(); }}>Add Entry</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatementUploader({ pos, orders, onImport, toast }) {
  const [parsed,   setParsed]   = useState([]);
  const [parsing,  setParsing]  = useState(false);
  const [fileName, setFileName] = useState("");
  const [selected, setSelected] = useState([]);
  const [editCat,  setEditCat]  = useState({});
  const fileRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    setParsed([]);
    try {
      let txns = [];
      if (file.name.toLowerCase().endsWith(".pdf")) {
        toast("🤖 Reading PDF with AI — may take 15-30 seconds…","success");
        txns = await parsePdfWithAI(file);
      } else {
        const buf  = await file.arrayBuffer();
        const wb   = XLSX.read(buf);
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header:1, defval:"" });
        const hdfcTxns = parseHDFCRows(rows);
        txns = hdfcTxns.length > 2 ? hdfcTxns : parseGenericRows(rows);
      }
      // match against POs
      txns = txns.map((t) => {
        if (t.type==="DR" && pos.length) {
          const match = pos.find((po) => Math.abs(po.totalAmount - t.amount) < 10 || (po.supplierName && t.description.toLowerCase().includes(po.supplierName.toLowerCase().slice(0,6))));
          if (match) t.matchedPO = match.poNumber;
        }
        if (t.type==="CR" && orders.length) {
          const match = orders.find((o) => Math.abs(parseFloat(o.total_price||o.totalAmount||0) - t.amount) < 50);
          if (match) t.matchedOrder = match.name || match.poNumber;
        }
        return t;
      });
      setParsed(txns);
      setSelected(txns.map((_,i)=>i));
      toast(`✅ Parsed ${txns.length} transactions`,"success");
    } catch(e) { toast("Failed to parse: "+e.message,"error"); }
    setParsing(false);
  };

  const toggle    = (i) => setSelected((p)=>p.includes(i)?p.filter((x)=>x!==i):[...p,i]);
  const toggleAll = () => setSelected(selected.length===parsed.length?[]:parsed.map((_,i)=>i));
  const totalIn   = parsed.filter((_,i)=>selected.includes(i)&&parsed[i].type==="CR").reduce((a,t)=>a+t.amount,0);
  const totalOut  = parsed.filter((_,i)=>selected.includes(i)&&parsed[i].type==="DR").reduce((a,t)=>a+t.amount,0);

  const doImport = () => {
    const toImport = parsed.filter((_,i)=>selected.includes(i)).map((t,i)=>({...t,category:editCat[i]||t.category}));
    onImport(toImport);
    setParsed([]); setSelected([]); setFileName(""); setEditCat({});
  };

  return (
    <div>
      <div style={{ border:"2px dashed #e5e7eb", borderRadius:10, padding:"2rem", textAlign:"center", cursor:"pointer", background:parsing?"#fff8f2":"#fafafa", marginBottom:14 }}
        onClick={()=>fileRef.current?.click()}
        onDragOver={(e)=>e.preventDefault()}
        onDrop={(e)=>{e.preventDefault();handleFile(e.dataTransfer.files[0]);}}>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.pdf" style={{ display:"none" }} onChange={(e)=>handleFile(e.target.files[0])}/>
        {parsing ? (
          <div><div style={{ fontSize:32, marginBottom:10 }}>⏳</div><div style={{ fontWeight:600 }}>Parsing {fileName} with AI…</div><div style={{ fontSize:12, color:"#9ca3af", marginTop:6 }}>Extracting all transactions</div></div>
        ) : (
          <div>
            <div style={{ fontSize:36, marginBottom:10 }}>🏦</div>
            <div style={{ fontWeight:600, fontSize:15, marginBottom:6 }}>Upload Bank Statement</div>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:10 }}>HDFC, SBI, ICICI, Axis, any bank · PDF or Excel/CSV</div>
            <div style={{ display:"flex", justifyContent:"center", gap:8 }}>
              {["📄 PDF (AI-parsed)","📊 Excel","📋 CSV"].map((f)=>(
                <span key={f} style={{ background:"#f3f4f6", border:"1px solid #e5e7eb", borderRadius:6, padding:"3px 10px", fontSize:11 }}>{f}</span>
              ))}
            </div>
            {fileName && <div style={{ fontSize:12, color:"#F47920", marginTop:8, fontWeight:600 }}>Last file: {fileName}</div>}
          </div>
        )}
      </div>

      {!parsed.length && !parsing && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
          {[["⬆","Upload","PDF or Excel from any bank"],["🤖","AI Parses","Reads every debit and credit"],["🏷️","Auto-tags","Salary, packaging, fees, etc."],["🔗","Matches","Links to your POs and orders"]].map(([icon,title,desc])=>(
            <div key={title} style={{ background:"#f9fafb", borderRadius:8, padding:"12px 14px", textAlign:"center" }}>
              <div style={{ fontSize:24, marginBottom:6 }}>{icon}</div>
              <div style={{ fontWeight:700, fontSize:12, marginBottom:3 }}>{title}</div>
              <div style={{ fontSize:11, color:"#6b7280" }}>{desc}</div>
            </div>
          ))}
        </div>
      )}

      {parsed.length > 0 && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
            {[["Transactions",parsed.length,"#1A3A5C"],["Selected",selected.length,"#6366f1"],["💰 Money In",INR(totalIn),"#15803d"],["💸 Money Out",INR(totalOut),"#F47920"]].map(([l,v,c])=>(
              <div key={l} style={{ background:"#fff", borderRadius:8, border:"1px solid #e5e7eb", padding:"10px 12px", borderTop:"3px solid "+c }}>
                <div style={{ fontSize:10, color:"#6b7280", marginBottom:2 }}>{l}</div>
                <div style={{ fontSize:16, fontWeight:700, color:c }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={S.card}>
            <div style={S.cardHdr}>
              <span>Review — {parsed.length} transactions from {fileName}</span>
              <div style={{ display:"flex", gap:8 }}>
                <button style={S.btnOut} onClick={toggleAll}>{selected.length===parsed.length?"Deselect All":"Select All"}</button>
                <button style={S.btn()} onClick={doImport} disabled={!selected.length}>⬇ Import {selected.length} Selected</button>
              </div>
            </div>
            <div style={{ overflowX:"auto", maxHeight:440, overflowY:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead style={{ position:"sticky", top:0 }}><tr>
                  {["","Date","Description","Type","Amount","Category","Match","Balance"].map((h)=><th key={h} style={S.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {parsed.map((t,i)=>{
                    const isSel = selected.includes(i);
                    const cat   = CATS.find((c)=>c.name===(editCat[i]||t.category))||CATS[CATS.length-1];
                    return (
                      <tr key={i} style={{ background:isSel?"#fff":"#fafafa", cursor:"pointer" }} onClick={()=>toggle(i)}>
                        <td style={S.td}><input type="checkbox" checked={isSel} onChange={()=>toggle(i)} onClick={(e)=>e.stopPropagation()}/></td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280", whiteSpace:"nowrap" }}>{t.date}</td>
                        <td style={{ ...S.td, maxWidth:220, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          <div title={t.description}>{t.description}</div>
                          {t.matchedPO    && <div style={{ fontSize:10, color:"#6366f1" }}>🔗 PO: {t.matchedPO}</div>}
                          {t.matchedOrder && <div style={{ fontSize:10, color:"#15803d" }}>🔗 Order: {t.matchedOrder}</div>}
                        </td>
                        <td style={S.td}>
                          <span style={{ background:t.type==="CR"?"#f0fdf4":"#fff8f2", color:t.type==="CR"?"#15803d":"#F47920", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>
                            {t.type==="CR"?"💰 IN":"💸 OUT"}
                          </span>
                        </td>
                        <td style={{ ...S.td, fontWeight:700, color:t.type==="CR"?"#15803d":"#F47920" }}>
                          {t.type==="CR"?"+":"-"}{INR(t.amount)}
                        </td>
                        <td style={S.td} onClick={(e)=>e.stopPropagation()}>
                          <select style={{ fontSize:11, padding:"2px 6px", borderRadius:5, border:"1px solid #d1d5db", fontFamily:"inherit" }}
                            value={editCat[i]||t.category} onChange={(e)=>setEditCat((p)=>({...p,[i]:e.target.value}))}>
                            {CATS.map((c)=><option key={c.name} value={c.name}>{c.icon} {c.name}</option>)}
                          </select>
                        </td>
                        <td style={S.td}>
                          {t.matchedPO    && <span style={{ background:"#ede9fe", color:"#6366f1", padding:"1px 6px", borderRadius:8, fontSize:10, fontWeight:600 }}>PO</span>}
                          {t.matchedOrder && <span style={{ background:"#f0fdf4", color:"#15803d", padding:"1px 6px", borderRadius:8, fontSize:10, fontWeight:600 }}>Order</span>}
                          {!t.matchedPO && !t.matchedOrder && <span style={{ color:"#d1d5db", fontSize:11 }}>—</span>}
                        </td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280", fontFamily:"monospace" }}>{t.balance?INR(t.balance):"—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function PageExpenses({ toast, pos=[], orders=[] }) {
  const [tab,         setTab]        = useState("overview");
  const [expenses,    setExpenses]   = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [showModal,   setShowModal]  = useState(false);
  const [filterMonth, setFilterMonth]= useState(new Date().toISOString().slice(0,7));
  const [filterType,  setFilterType] = useState("all");
  const [filterCat,   setFilterCat]  = useState("all");
  const [search,      setSearch]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/expenses?month=${filterMonth}`);
      setExpenses(data.expenses || []);
    } catch(e) { toast(e.message,"error"); }
    setLoading(false);
  }, [filterMonth]);

  useEffect(() => { load(); }, [load]);

  const saveExpense = async (form) => {
    await api("/expenses", { method:"POST", body:JSON.stringify(form) });
    toast("Added ✓","success");
    load();
  };

  const importMany = async (txns) => {
    let ok = 0;
    for (const t of txns) {
      try {
        await api("/expenses", { method:"POST", body:JSON.stringify({
          date:t.date, category:t.category, description:t.description,
          amount:t.amount, type:t.type, payMode:t.payMode||"Bank Transfer",
          gstRate:0, isGstInput:false, gstAmount:0, vendor:"",
          notes:t.matchedPO?"Matched PO: "+t.matchedPO:t.matchedOrder?"Matched Order: "+t.matchedOrder:"",
          source:"statement",
        })});
        ok++;
      } catch {}
    }
    toast(`Imported ${ok} of ${txns.length} transactions ✓`,"success");
    load();
    setTab("ledger");
  };

  // Repair existing rows where statement import set the wrong CR/DR direction.
  // Detects from narration ("NEFT CR-" = money IN) and re-saves corrected rows.
  // Detect rows with wrong IN/OUT direction (for the alert banner)
  const wrongDirectionCount = expenses.filter((e) => {
    const sc = /\bCR[-\s]|CREDIT|\bREV\b|REFUND|CASHBACK/.test((e.description||"").toUpperCase());
    const sd = /\bDR[-\s]|DEBIT|\bPUR\b|\bDR\//.test((e.description||"").toUpperCase());
    if (e.category === "Sales / Payment In" && e.type !== "CR") return true;
    if (sc && !sd && e.type !== "CR") return true;
    if (sd && !sc && e.type !== "DR") return true;
    return false;
  }).length;

  const fixDirections = async () => {
    const narrSaysCredit = (d) => /\bCR[-\s]|CREDIT|\bREV\b|REFUND|CASHBACK/.test((d||"").toUpperCase());
    const narrSaysDebit  = (d) => /\bDR[-\s]|DEBIT|\bPUR\b|\bDR\//.test((d||"").toUpperCase());
    const wrong = expenses.filter((e) => {
      const sc = narrSaysCredit(e.description), sd = narrSaysDebit(e.description);
      // Category contradiction: tagged as income but marked OUT
      if (e.category === "Sales / Payment In" && e.type !== "CR") return true;
      if (sc && !sd && e.type !== "CR") return true;   // narration says credit but isn't IN
      if (sd && !sc && e.type !== "DR") return true;   // narration says debit but isn't OUT
      return false;
    });
    if (wrong.length === 0) { toast("No direction errors found ✓","success"); return; }
    if (!window.confirm(`Found ${wrong.length} transactions with the wrong IN/OUT direction (e.g. Myntra/Razorpay payments marked as expense). Fix them now?`)) return;
    let fixed = 0, failed = 0, lastErr = "";
    for (const e of wrong) {
      const id = e._id || e.id;
      if (!id) { failed++; lastErr = "row has no id"; continue; }
      const newType = "CR"; // these are all money-received rows
      const newCategory = "Sales / Payment In";
      // Try an in-place update first (PUT) — safest, no data loss
      try {
        await api("/expenses/"+id, {
          method: "PUT",
          body: JSON.stringify({ ...e, type: newType, category: newCategory }),
        });
        fixed++;
        continue;
      } catch (err1) {
        // PUT not supported — fall back to delete + recreate
        try {
          await api("/expenses", { method:"POST", body:JSON.stringify({
            date:e.date, category:newCategory, description:e.description,
            amount:Math.abs(num(e.amount)), type:newType,
            payMode:e.payMode||"Bank Transfer", gstRate:0, isGstInput:false, gstAmount:0,
            vendor:e.vendor||"", notes:e.notes||"", source:e.source||"statement",
          })});
          await api("/expenses/"+id, { method:"DELETE" });
          fixed++;
        } catch (err2) {
          failed++; lastErr = (err2 && err2.message) || (err1 && err1.message) || "unknown";
        }
      }
    }
    if (failed > 0) {
      toast(`Fixed ${fixed}, but ${failed} failed (${lastErr}). Try again or check connection.`, failed > fixed ? "error" : "success");
    } else {
      toast(`Fixed all ${fixed} transactions ✓`, "success");
    }
    await load();
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api("/expenses/"+id,{method:"DELETE"});
    toast("Deleted","success");
    load();
  };

  const filtered = expenses.filter((e) => {
    if (filterType!=="all" && e.type!==filterType) return false;
    if (filterCat!=="all" && e.category!==filterCat) return false;
    if (search) { const q=search.toLowerCase(); return (e.description||"").toLowerCase().includes(q)||(e.vendor||"").toLowerCase().includes(q); }
    return true;
  });

  const totalIn  = expenses.filter((e)=>e.type==="CR").reduce((a,e)=>a+e.amount,0);
  const totalOut = expenses.filter((e)=>e.type==="DR"||!e.type).reduce((a,e)=>a+e.amount,0);
  const net      = totalIn - totalOut;

  const now = new Date();
  const monthlyData = Array.from({length:6},(_,i)=>{
    const d   = new Date(now.getFullYear(), now.getMonth()-5+i, 1);
    const key = d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0");
    const rows= expenses.filter((e)=>e.date?.startsWith(key));
    return { label:MONTHS[d.getMonth()], in:rows.filter((e)=>e.type==="CR").reduce((a,e)=>a+e.amount,0), out:rows.filter((e)=>e.type==="DR"||!e.type).reduce((a,e)=>a+e.amount,0) };
  });

  const catBreakdown = CATS.map((c)=>{
    const rows = expenses.filter((e)=>e.category===c.name&&(e.type==="DR"||!e.type));
    return { ...c, total:rows.reduce((a,e)=>a+e.amount,0), count:rows.length };
  }).filter((c)=>c.total>0).sort((a,b)=>b.total-a.total);

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ["Date","Type","Description","Category","Amount","Vendor","Pay Mode","Source","Notes"],
      ...filtered.map((e)=>[e.date,e.type==="CR"?"Money In":"Money Out",e.description,e.category,e.amount,e.vendor||"",e.payMode||"",e.source||"manual",e.notes||""])
    ]);
    ws["!cols"]=[{wch:12},{wch:12},{wch:40},{wch:20},{wch:14},{wch:20},{wch:14},{wch:10},{wch:30}];
    XLSX.utils.book_append_sheet(wb,ws,"Expenses");
    XLSX.writeFile(wb,`3June_Expenses_${filterMonth}.xlsx`);
    toast("Excel downloaded ✓","success");
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>💸 Expenses & Cash Flow</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Cash entries · Bank statement upload (PDF/Excel) · Auto-categorize · Match POs & orders</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button style={S.btnOut} onClick={exportExcel}>⬇ Excel</button>
          <button style={{ ...S.btn("#1A3A5C") }} onClick={()=>setTab("upload")}>🏦 Upload Statement</button>
          <button style={S.btn()} onClick={()=>setShowModal(true)}>+ Add Cash Entry</button>
        </div>
      </div>

      {/* Wrong-direction alert — money received marked as expense */}
      {wrongDirectionCount > 0 && (
        <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"12px 16px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          <div style={{ fontSize:13, color:"#991b1b" }}>
            ⚠️ <strong>{wrongDirectionCount} transactions</strong> look like money you <em>received</em> (Myntra, Razorpay, Delhivery) but are marked as expenses (OUT) and being deducted.
          </div>
          <button onClick={fixDirections} style={{ background:"#dc2626", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
            🔧 Fix {wrongDirectionCount} now
          </button>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
        {[
          { label:"💰 Money In",  value:INR(totalIn),          color:"#15803d", sub:expenses.filter((e)=>e.type==="CR").length+" txns" },
          { label:"💸 Money Out", value:INR(totalOut),         color:"#F47920", sub:expenses.filter((e)=>e.type!=="CR").length+" txns" },
          { label:"📊 Net Flow",  value:INR(Math.abs(net)),    color:net>=0?"#15803d":"#dc2626", sub:net>=0?"▲ Surplus":"▼ Deficit" },
          { label:"📋 Entries",   value:expenses.length,       color:"#6366f1", sub:"this month" },
        ].map((k)=>(
          <div key={k.label} style={{ ...S.card, padding:"12px 14px", marginBottom:0, borderTop:"3px solid "+k.color }}>
            <div style={{ fontSize:10, color:"#6b7280", marginBottom:2 }}>{k.label}</div>
            <div style={{ fontSize:18, fontWeight:700, color:k.color }}>{loading?"—":k.value}</div>
            <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16 }}>
        {[["overview","📊 Overview"],["ledger","📋 Ledger"],["upload","🏦 Bank Statement"],["itc","🧾 GST ITC"]].map(([id,label])=>(
          <button key={id} style={S.tab(tab===id)} onClick={()=>setTab(id)}>{label}</button>
        ))}
      </div>

      {tab==="overview" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div style={S.card}>
            <div style={S.cardHdr}>
              <span>Cash Flow — Last 6 Months</span>
              <div style={{ display:"flex", gap:10, fontSize:11 }}><span style={{ color:"#22c55e" }}>█ In</span><span style={{ color:"#F47920" }}>█ Out</span></div>
            </div>
            <div style={{ padding:16 }}><BarChart data={monthlyData}/></div>
          </div>
          <div style={S.card}>
            <div style={S.cardHdr}>
              <span>Expense Breakdown</span>
              <input type="month" value={filterMonth} onChange={(e)=>setFilterMonth(e.target.value)} style={{ padding:"3px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12 }}/>
            </div>
            <div style={{ padding:14 }}>
              {catBreakdown.length===0 ? <div style={{ textAlign:"center", padding:24, color:"#9ca3af" }}>No expenses this month</div>
              : catBreakdown.slice(0,7).map((c)=>{
                  const pct=totalOut?Math.round(c.total/totalOut*100):0;
                  return (
                    <div key={c.name} style={{ marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:3 }}>
                        <span>{c.icon} {c.name}</span>
                        <span style={{ fontWeight:600 }}>{INR(c.total)} <span style={{ color:"#9ca3af", fontWeight:400 }}>({pct}%)</span></span>
                      </div>
                      <div style={{ background:"#f3f4f6", borderRadius:10, height:5 }}>
                        <div style={{ background:c.color, borderRadius:10, height:5, width:pct+"%", transition:"width .4s" }}/>
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </div>
          <div style={{ ...S.card, gridColumn:"1/-1" }}>
            <div style={S.cardHdr}><span>Recent Transactions</span><button style={S.btnOut} onClick={()=>setTab("ledger")}>View All</button></div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>{["Date","Type","Description","Category","Amount","Source"].map((h)=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {expenses.slice(0,8).map((e,i)=>{
                    const cat=CATS.find((c)=>c.name===e.category)||CATS[CATS.length-1];
                    return (
                      <tr key={i} onMouseEnter={(ev)=>ev.currentTarget.style.background="#fafafa"} onMouseLeave={(ev)=>ev.currentTarget.style.background=""}>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{new Date(e.date).toLocaleDateString("en-IN")}</td>
                        <td style={S.td}><span style={{ background:e.type==="CR"?"#f0fdf4":"#fff8f2", color:e.type==="CR"?"#15803d":"#F47920", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>{e.type==="CR"?"💰 IN":"💸 OUT"}</span></td>
                        <td style={{ ...S.td, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{e.description}</td>
                        <td style={S.td}><span style={{ background:cat.color+"15", color:cat.color, padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:600 }}>{cat.icon} {e.category}</span></td>
                        <td style={{ ...S.td, fontWeight:700, color:e.type==="CR"?"#15803d":"#F47920" }}>{e.type==="CR"?"+":"-"}{INR(e.amount)}</td>
                        <td style={{ ...S.td, fontSize:10, color:"#9ca3af" }}>{e.source==="statement"?"🏦 Bank":"✏️ Manual"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab==="ledger" && (
        <>
          <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
            <input type="month" value={filterMonth} onChange={(e)=>setFilterMonth(e.target.value)} style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12 }}/>
            <select style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12 }} value={filterType} onChange={(e)=>setFilterType(e.target.value)}>
              <option value="all">All Types</option><option value="DR">💸 Money Out</option><option value="CR">💰 Money In</option>
            </select>
            <select style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12 }} value={filterCat} onChange={(e)=>setFilterCat(e.target.value)}>
              <option value="all">All Categories</option>
              {CATS.map((c)=><option key={c.name} value={c.name}>{c.icon} {c.name}</option>)}
            </select>
            <input style={{ flex:1, minWidth:160, padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12 }} placeholder="Search description or vendor…" value={search} onChange={(e)=>setSearch(e.target.value)}/>
            <span style={{ fontSize:12, color:"#6b7280" }}>{filtered.length} entries</span>
            <button onClick={fixDirections} title="Fix transactions where money received (Myntra/Razorpay) was wrongly marked as an expense"
              style={{ padding:"7px 12px", borderRadius:7, border:"1px solid #fed7aa", background:"#fff7ed", color:"#F47920", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
              🔧 Fix IN/OUT
            </button>
          </div>
          <div style={S.card}>
            {loading ? <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Loading…</div>
            : filtered.length===0
            ? <EmptyState
                icon="💸"
                title="No expenses recorded yet"
                subtitle="Track every business expense — rent, salaries, ad spend, packaging. See where your money goes and claim GST input credit."
                actionLabel="+ Add your first expense"
                onAction={()=>setShowModal(true)}
                columns={["Date","Type","Description","Category","Amount"]}
                exampleRows={[
                  ["12 Jun","OUT","Meta ad spend","Marketing","₹4,500"],
                  ["11 Jun","OUT","Courier charges","Logistics","₹2,800"],
                  ["10 Jun","OUT","Packaging material","Supplies","₹1,200"],
                ]}
              />
            : <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>{["Date","Type","Description","Category","Amount","Pay Mode","Source",""].map((h)=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {filtered.map((e)=>{
                      const cat=CATS.find((c)=>c.name===e.category)||CATS[CATS.length-1];
                      return (
                        <tr key={e._id} onMouseEnter={(ev)=>ev.currentTarget.style.background="#fafafa"} onMouseLeave={(ev)=>ev.currentTarget.style.background=""}>
                          <td style={{ ...S.td, fontSize:11, color:"#6b7280", whiteSpace:"nowrap" }}>{new Date(e.date).toLocaleDateString("en-IN")}</td>
                          <td style={S.td}><span style={{ background:e.type==="CR"?"#f0fdf4":"#fff8f2", color:e.type==="CR"?"#15803d":"#F47920", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>{e.type==="CR"?"💰 IN":"💸 OUT"}</span></td>
                          <td style={{ ...S.td, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                            <div title={e.description}>{e.description}</div>
                            {e.notes && <div style={{ fontSize:10, color:"#6366f1" }}>{e.notes}</div>}
                          </td>
                          <td style={S.td}><span style={{ background:cat.color+"15", color:cat.color, padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:600 }}>{cat.icon} {e.category}</span></td>
                          <td style={{ ...S.td, fontWeight:700, color:e.type==="CR"?"#15803d":"#F47920" }}>{e.type==="CR"?"+":"-"}{INR(e.amount)}</td>
                          <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{e.payMode||"—"}</td>
                          <td style={{ ...S.td, fontSize:10, color:"#9ca3af" }}>{e.source==="statement"?"🏦 Bank":"✏️ Manual"}</td>
                          <td style={S.td}><button style={{ ...S.btnOut, padding:"2px 8px", fontSize:11, color:"#dc2626", borderColor:"#fecaca" }} onClick={()=>deleteExpense(e._id)}>Del</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot><tr style={{ background:"#f9fafb" }}>
                    <td colSpan={4} style={{ ...S.td, fontWeight:700 }}>Total ({filtered.length})</td>
                    <td style={S.td}>
                      <div style={{ color:"#15803d", fontSize:11, fontWeight:700 }}>+{INR(filtered.filter((e)=>e.type==="CR").reduce((a,e)=>a+e.amount,0))}</div>
                      <div style={{ color:"#F47920", fontSize:11, fontWeight:700 }}>-{INR(filtered.filter((e)=>e.type!=="CR").reduce((a,e)=>a+e.amount,0))}</div>
                    </td>
                    <td colSpan={3} style={S.td}/>
                  </tr></tfoot>
                </table>
              </div>
            }
          </div>
        </>
      )}

      {tab==="upload" && <StatementUploader pos={pos} orders={orders} onImport={importMany} toast={toast}/>}

      {tab==="itc" && (() => {
        const itcRows  = expenses.filter((e)=>e.isGstInput&&e.gstAmount>0);
        const itcTotal = itcRows.reduce((a,e)=>a+e.gstAmount,0);
        return (
          <>
            <div style={{ ...S.card, padding:14, borderLeft:"4px solid #15803d", marginBottom:14 }}>
              <div style={{ fontWeight:600, color:"#15803d", marginBottom:4 }}>🧾 Input Tax Credit — {filterMonth}</div>
              <div style={{ fontSize:12, color:"#6b7280" }}>GST paid on business purchases. Only manual entries with "GST Invoice" checked appear here. Total claimable: <strong style={{ color:"#15803d" }}>{INR(itcTotal)}</strong></div>
            </div>
            {itcRows.length===0
              ? <div style={{ ...S.card, padding:50, textAlign:"center" }}>
                  <div style={{ fontSize:36, marginBottom:10 }}>🧾</div>
                  <div style={{ fontWeight:600, marginBottom:6 }}>No ITC bills this period</div>
                  <button style={S.btn()} onClick={()=>setShowModal(true)}>+ Add GST Expense</button>
                </div>
              : <div style={S.card}>
                  <div style={S.cardHdr}><span>ITC Claimable Entries</span><span style={{ fontWeight:700, color:"#15803d" }}>{INR(itcTotal)}</span></div>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead><tr>{["Date","Vendor","Category","Invoice","Taxable","GST Rate","GST (ITC)"].map((h)=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {itcRows.map((e)=>(
                          <tr key={e._id} onMouseEnter={(ev)=>ev.currentTarget.style.background="#f0fdf4"} onMouseLeave={(ev)=>ev.currentTarget.style.background=""}>
                            <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{new Date(e.date).toLocaleDateString("en-IN")}</td>
                            <td style={S.td}>{e.vendor||"—"}</td>
                            <td style={S.td}>{e.category}</td>
                            <td style={{ ...S.td, fontSize:11, fontFamily:"monospace" }}>{e.invoiceNo||"—"}</td>
                            <td style={{ ...S.td, fontWeight:600 }}>{INR(e.amount-e.gstAmount)}</td>
                            <td style={{ ...S.td, fontSize:11 }}>{e.gstRate}%</td>
                            <td style={{ ...S.td, fontWeight:700, color:"#15803d" }}>{INR(e.gstAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr style={{ background:"#f0fdf4" }}>
                        <td colSpan={6} style={{ ...S.td, fontWeight:700 }}>Total ITC Claimable</td>
                        <td style={{ ...S.td, fontWeight:700, fontSize:15, color:"#15803d" }}>{INR(itcTotal)}</td>
                      </tr></tfoot>
                    </table>
                  </div>
                </div>
            }
          </>
        );
      })()}

      {showModal && <AddExpenseModal onSave={saveExpense} onClose={()=>setShowModal(false)} toast={toast}/>}
    </div>
  );
}
